import {
  emitWebVitalIfValueChanged,
  emitWebVitalOnce,
  finalizeView,
  isViewEndEvent,
  shouldIgnoreViewUpdate,
} from './datadog-rum-web-vital-dedup'
import { TELEMETRY_EVENT_NAMES } from './telemetry-events.constants'
import { trackInternalEvent } from './track'

import type { RumBeforeSend, RumViewEvent } from '@datadog/browser-rum'

const NS_PER_MS = 1_000_000

// Pulse custom metrics (`performance_tracker_fcp_v2` / `_lcp_v2` / `_cls_v2` /
// `_inp_v2` / `_ttfb_v2`) need Statsig logEvent(name, value). The `_v2` suffix
// avoids colliding with existing Pulse metrics on the unversioned names.
// Datadog's beforeSend hook is not React, so the user-aware provider installs
// the client logger here — same module-level cache as
// setTelemetryFanOutDeferralEnabled.
let statsigLogEvent: ((eventName: string, value?: string | number) => void) | undefined

export const setStatsigLogEvent = (
  logEvent: ((eventName: string, value?: string | number) => void) | undefined,
): void => {
  statsigLogEvent = logEvent
}

function logStatsigWebVital(vital: 'fcp' | 'lcp' | 'cls' | 'inp' | 'ttfb', value: number) {
  statsigLogEvent?.(`performance_tracker_${vital}_v2`, value)
}

/**
 * Forward Datadog RUM web vitals from view events into Internal Analytics.
 */
export const datadogRumBeforeSend: RumBeforeSend = (event, _context) => {
  if (event.type !== 'view') {
    return true
  }

  trackWebVitalsFromViewEvent(event)
  return true
}

function trackWebVitalsFromViewEvent(event: RumViewEvent) {
  const viewId = event.view.id
  if (!viewId || shouldIgnoreViewUpdate(viewId)) {
    return
  }

  const { performance } = event.view

  const fcp = performance?.fcp?.timestamp
  if (fcp != null) {
    // eslint-disable-next-line i18next/no-literal-string
    emitWebVitalOnce(viewId, 'fcp', () => {
      const fcpMs = Math.round(fcp / NS_PER_MS)
      trackInternalEvent(TELEMETRY_EVENT_NAMES.FCP_EVENT, { fcp: fcpMs })
      logStatsigWebVital('fcp', fcpMs)
    })
  }

  const lcp = performance?.lcp?.timestamp
  if (lcp != null) {
    // eslint-disable-next-line i18next/no-literal-string
    emitWebVitalOnce(viewId, 'lcp', () => {
      const lcpMs = Math.round(lcp / NS_PER_MS)
      trackInternalEvent(TELEMETRY_EVENT_NAMES.LCP_EVENT, { lcp: lcpMs })
      logStatsigWebVital('lcp', lcpMs)
    })
  }

  const cls = performance?.cls?.score
  if (cls != null) {
    // eslint-disable-next-line i18next/no-literal-string
    emitWebVitalIfValueChanged(viewId, 'cls', cls, () => {
      trackInternalEvent(TELEMETRY_EVENT_NAMES.CLS_EVENT, { cls })
      logStatsigWebVital('cls', cls)
    })
  }

  const inp = performance?.inp?.duration
  if (inp != null) {
    const inpMs = Math.round(inp / NS_PER_MS)
    // eslint-disable-next-line i18next/no-literal-string
    emitWebVitalIfValueChanged(viewId, 'inp', inpMs, () => {
      trackInternalEvent(TELEMETRY_EVENT_NAMES.INP_EVENT, { inp: inpMs })
      logStatsigWebVital('inp', inpMs)
    })
  }

  const ttfb = event.view.first_byte
  if (ttfb != null) {
    // eslint-disable-next-line i18next/no-literal-string
    emitWebVitalOnce(viewId, 'ttfb', () => {
      const ttfbMs = Math.round(ttfb / NS_PER_MS)
      trackInternalEvent(TELEMETRY_EVENT_NAMES.TTFB_EVENT, { ttfb: ttfbMs })
      logStatsigWebVital('ttfb', ttfbMs)
    })
  }

  if (isViewEndEvent(event)) {
    finalizeView(viewId)
  }
}
